/**
 * Company Setup View/Edit Page
 * Beautiful interface for viewing and editing company information
 * Features: Field locking, edit requests, version history, locked field indicators
 */

/* eslint-disable prettier/prettier */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  Button,
  Input,
  Field,
  Text,
  Spinner,
  Badge,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Textarea,
  Avatar,
} from '@fluentui/react-components';
import {
  BuildingRegular,
  DocumentTextRegular,
  ColorRegular,
  LockClosedRegular,
  EditRegular,
  HistoryRegular,
  PhoneRegular,
  MailRegular,
  CheckmarkCircleRegular,
  ClockRegular,
  SaveRegular,
  InfoRegular,
} from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { SearchableDropdown } from '../../../components/common';
import { ApiClient, newToast, normalizeImageUrl } from '../../../configurations/index.config';
import { countries } from '../../../configurations/countries';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '32px',
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  logoContainer: {
    width: '80px',
    height: '80px',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '2px solid #e1dfdd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
  },
  logo: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  headerInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  companyName: {
    fontSize: '26px',
    fontWeight: '700',
    color: '#323130',
    background: 'linear-gradient(135deg, #008272 0%, #00635a 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  tagline: {
    fontSize: '14px',
    color: '#605e5c',
    fontStyle: 'italic',
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
  },
  primaryButton: {
    backgroundColor: '#008272',
    color: '#ffffff',
    fontWeight: '600',
    ':hover': {
      backgroundColor: '#00635a',
    },
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    color: '#008272',
    border: '1px solid #008272',
    fontWeight: '600',
    ':hover': {
      backgroundColor: 'rgba(0, 130, 114, 0.05)',
    },
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  gridLayout: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
    gap: '24px',
    marginBottom: '24px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '2px solid #e1dfdd',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '20px',
    fontWeight: '600',
    color: '#323130',
  },
  sectionIcon: {
    fontSize: '24px',
    color: '#008272',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  fieldRow: {
    display: 'flex',
    gap: '16px',
    width: '100%',
  },
  fieldFull: {
    flex: '1 1 100%',
  },
  fieldHalf: {
    flex: '1 1 50%',
  },
  readOnlyField: {
    backgroundColor: '#f5f5f5',
    borderTopColor: '#e1dfdd',
    borderRightColor: '#e1dfdd',
    borderBottomColor: '#e1dfdd',
    borderLeftColor: '#e1dfdd',
  },
  lockedField: {
    position: 'relative',
  },
  lockIndicator: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: '#605e5c',
    fontSize: '12px',
    backgroundColor: '#fef3cd',
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid #fde68a',
  },
  colorDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  colorSwatch: {
    width: '48px',
    height: '48px',
    borderRadius: '8px',
    border: '2px solid #e1dfdd',
  },
  colorInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  arrayItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: '#f5f5f5',
    borderRadius: '6px',
    border: '1px solid #e1dfdd',
  },
  arrayItemIcon: {
    fontSize: '16px',
    color: '#008272',
  },
  versionHistoryCard: {
    marginTop: '24px',
  },
  versionItem: {
    padding: '16px',
    borderBottom: '1px solid #e1dfdd',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    ':last-child': {
      borderBottom: 'none',
    },
  },
  versionAvatar: {
    flexShrink: 0,
  },
  versionContent: {
    flex: 1,
  },
  versionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
  },
  versionNumber: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#323130',
  },
  versionDate: {
    fontSize: '13px',
    color: '#605e5c',
  },
  versionChanges: {
    fontSize: '14px',
    color: '#323130',
    marginBottom: '8px',
  },
  versionMeta: {
    fontSize: '12px',
    color: '#605e5c',
  },
  editRequestDialog: {
    minWidth: '500px',
  },
  dialogField: {
    marginBottom: '16px',
  },
  requestStatusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  pendingBadge: {
    backgroundColor: '#fef3cd',
    color: '#92400e',
  },
  approvedBadge: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
  },
  rejectedBadge: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 24px',
    color: '#605e5c',
    textAlign: 'center',
    gap: '16px',
  },
  emptyIcon: {
    fontSize: '80px',
    color: '#c8c6c4',
  },
});

interface CompanySetup {
  id: string;
  legal_name: string;
  tin: string;
  tax_registration_number: string;
  registered_address: string;
  country: string;
  primary_phone: string;
  secondary_phones: string[];
  primary_email: string;
  secondary_emails: string[];
  website: string;
  logo_url: string;
  logo_thumbnail_url: string;
  tagline: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  invoice_prefix: string;
  memo_prefix: string;
  incident_prefix: string;
  contract_prefix: string;
  current_version: string;
  setup_completed_at: string;
  setup_completed_by: string;
  last_updated_at: string;
  last_updated_by: string;
}

interface VersionHistory {
  id: string;
  version: string;
  changed_by: string;
  changed_at: string;
  change_reason: string;
  changed_fields: string[];
}

interface EditRequest {
  id: string;
  field_name: string;
  current_value: string;
  requested_value: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired' | 'completed';
  requested_at: string;
  requested_by: string;
  reviewed_at?: string;
  reviewed_by?: string;
  reviewer_comments?: string;
}

const LOCKED_FIELDS = [
  'legal_name',
  'tin',
  'tax_registration_number',
  'registered_address',
  'country',
  'primary_email',
];

const FIELD_LABELS: Record<string, string> = {
  legal_name: 'Legal Name',
  tin: 'Tax Identification Number',
  tax_registration_number: 'Tax Registration Number',
  registered_address: 'Registered Address',
  country: 'Country',
  primary_email: 'Primary Email',
  primary_phone: 'Primary Phone',
  website: 'Website',
  tagline: 'Tagline',
  primary_color: 'Primary Color',
  secondary_color: 'Secondary Color',
  accent_color: 'Accent Color',
  invoice_prefix: 'Invoice Prefix',
  memo_prefix: 'Memo Prefix',
  incident_prefix: 'Incident Prefix',
  contract_prefix: 'Contract Prefix',
};

export const CompanySetupView: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companyData, setCompanyData] = useState<CompanySetup | null>(null);
  const [versionHistory, setVersionHistory] = useState<VersionHistory[]>([]);
  const [editRequests, setEditRequests] = useState<EditRequest[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState<Partial<CompanySetup>>({});

  // Edit Request Dialog
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [requestField, setRequestField] = useState('');
  const [requestValue, setRequestValue] = useState('');
  const [requestReason, setRequestReason] = useState('');

  useEffect(() => {
    loadCompanyData();
    loadVersionHistory();
    loadEditRequests();
  }, []);

  const loadCompanyData = async () => {
    setLoading(true);
    try {
      console.log('Loading company data from:', '/api/v1/company-setup');
      const response = await ApiClient.get('/api/v1/company-setup');
      console.log('Company data response:', response);
      console.log('Response data:', response.data);
      
      // Try multiple possible response structures
      let companyData = null;
      
      if (response.data.company) {
        console.log('Found company in response.data.company');
        companyData = response.data.company;
      } else if (response.data.data) {
        console.log('Found data in response.data.data');
        // Check if data has company info directly
        if (response.data.data.company_id || response.data.data.legal_name) {
          companyData = response.data.data;
        }
      } else if (response.data.setup) {
        console.log('Found setup in response.data.setup');
        companyData = response.data.setup;
      }
      
      if (companyData) {
        console.log('Setting company data:', companyData);
        setCompanyData(companyData);
        setEditFormData(companyData);
      } else {
        console.warn('No company data found in any expected location');
        console.warn('Full response.data:', JSON.stringify(response.data, null, 2));
        // If backend returns empty data object, it likely means no company exists
        setCompanyData(null);
      }
    } catch (error: any) {
      console.error('Error loading company data:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      // If 404, no setup exists - show empty state with button to start wizard
      if (error.response?.status === 404) {
        console.log('404 - Company not found, showing empty state');
        setCompanyData(null);
      } else {
        newToast('error', getErrorMessage(error));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadVersionHistory = async () => {
    try {
      const response = await ApiClient.get('/api/v1/company-setup/versions?limit=10&offset=0');
      setVersionHistory(response.data.versions || []);
    } catch (error) {
      console.error('Failed to load version history:', error);
    }
  };

  const loadEditRequests = async () => {
    try {
      const response = await ApiClient.get('/api/v1/company-setup/edit-requests?status=pending');
      setEditRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to load edit requests:', error);
    }
  };

  const isFieldLocked = (field: string): boolean => {
    return LOCKED_FIELDS.includes(field);
  };

  const handleEditToggle = () => {
    if (editMode) {
      setEditFormData(companyData || {});
    }
    setEditMode(!editMode);
  };

  const handleFieldUpdate = (field: keyof CompanySetup, value: any) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const changes: any = {};

      // Only include changed fields
      Object.keys(editFormData).forEach(key => {
        if (editFormData[key as keyof CompanySetup] !== companyData?.[key as keyof CompanySetup]) {
          changes[key] = editFormData[key as keyof CompanySetup];
        }
      });

      if (Object.keys(changes).length === 0) {
        newToast('info', 'No changes to save');
        setEditMode(false);
        return;
      }

      await ApiClient.patch('/api/v1/company-setup', { fields: changes });

      newToast('success', 'Company information updated successfully');
      setEditMode(false);
      loadCompanyData();
      loadVersionHistory();
    } catch (error: any) {
      if (error.response?.data?.locked_fields) {
        newToast('error', 'Some fields are locked. Please request edit approval.');
      } else {
        newToast('error', getErrorMessage(error));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRequestEdit = async () => {
    if (!requestField || !requestValue || !requestReason) {
      newToast('error', 'Please fill in all fields');
      return;
    }

    try {
      await ApiClient.post('/api/v1/company-setup/request-edit', {
        field_name: requestField,
        requested_value: requestValue,
        reason: requestReason,
      });

      newToast('success', 'Edit request submitted successfully');
      setRequestDialogOpen(false);
      setRequestField('');
      setRequestValue('');
      setRequestReason('');
      loadEditRequests();
    } catch (error) {
      newToast('error', getErrorMessage(error));
    }
  };

  const openRequestDialog = (field: string, currentValue: string) => {
    setRequestField(field);
    setRequestValue(currentValue);
    setRequestReason('');
    setRequestDialogOpen(true);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Spinner size="large" label="Loading company information..." />
        </div>
      </div>
    );
  }

  if (!companyData) {
    return (
      <div className={styles.container}>
        <ContentCard>
          <div className={styles.emptyState}>
            <BuildingRegular className={styles.emptyIcon} />
            <Text style={{ fontSize: '20px', fontWeight: '600', color: '#323130' }}>
              No Company Setup Found
            </Text>
            <Text style={{ fontSize: '14px', color: '#605e5c' }}>
              Complete the setup wizard to configure your company information.
            </Text>
            <Button
              className={styles.primaryButton}
              onClick={() => navigate('/account/company-setup/wizard')}
            >
              Start Setup Wizard
            </Button>
          </div>
        </ContentCard>
      </div>
    );
  }

  const countryLabel =
    countries.find(c => c.value === companyData.country)?.label || companyData.country;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.headerLeft}>
            <div className={styles.logoContainer}>
              {companyData.logo_url ? (
                <img src={normalizeImageUrl(companyData.logo_url)} alt="Company Logo" className={styles.logo} />
              ) : (
                <BuildingRegular style={{ fontSize: '40px', color: '#c8c6c4' }} />
              )}
            </div>
            <div className={styles.headerInfo}>
              <Text className={styles.companyName}>{companyData.legal_name}</Text>
              {companyData.tagline && (
                <Text className={styles.tagline}>"{companyData.tagline}"</Text>
              )}
              <div className={styles.statusBadge}>
                <Badge appearance="filled" color="success">
                  <CheckmarkCircleRegular style={{ fontSize: '14px', marginRight: '4px' }} />
                  Setup Complete
                </Badge>
                <Text style={{ fontSize: '13px', color: '#605e5c' }}>
                  Version {companyData.current_version}
                </Text>
              </div>
            </div>
          </div>

          <div className={styles.headerActions}>
            <Button
              className={styles.secondaryButton}
              icon={<HistoryRegular />}
              onClick={() => {
                const historySection = document.getElementById('version-history');
                historySection?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              View History
            </Button>
            <Button
              className={styles.primaryButton}
              icon={<EditRegular />}
              onClick={() => navigate('/account/company-setup/wizard?mode=edit')}
            >
              Edit Company Records
            </Button>
          </div>
        </div>
      </div>

      {editRequests.length > 0 && (
        <ContentCard
          title="Pending Edit Requests"
          subtitle="You have pending requests for locked fields"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {editRequests.map(request => (
              <div
                key={request.id}
                style={{
                  padding: '12px',
                  backgroundColor: '#fef3cd',
                  borderRadius: '8px',
                  border: '1px solid #fde68a',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <Text style={{ fontWeight: '600' }}>{FIELD_LABELS[request.field_name]}</Text>
                  <Text style={{ fontSize: '13px', color: '#605e5c', display: 'block' }}>
                    Requested: {new Date(request.requested_at).toLocaleDateString()}
                  </Text>
                </div>
                <Badge appearance="filled" color="warning">
                  <ClockRegular style={{ fontSize: '12px', marginRight: '4px' }} />
                  Pending Review
                </Badge>
              </div>
            ))}
          </div>
        </ContentCard>
      )}

      <div className={styles.gridLayout}>
        <ContentCard>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>
                <BuildingRegular className={styles.sectionIcon} />
                <Text>Legal Information</Text>
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <Field label="Legal Name">
                <div className={styles.lockedField}>
                  <Input
                    value={editMode ? editFormData.legal_name : companyData.legal_name}
                    onChange={e => handleFieldUpdate('legal_name', e.target.value)}
                    readOnly={!editMode}
                    className={!editMode ? styles.readOnlyField : ''}
                  />
                  {isFieldLocked('legal_name') && (
                    <>
                      <div className={styles.lockIndicator}>
                        <LockClosedRegular style={{ fontSize: '12px' }} />
                        Locked
                      </div>
                      {editMode && (
                        <Button
                          size="small"
                          appearance="subtle"
                          onClick={() => openRequestDialog('legal_name', companyData.legal_name)}
                          style={{ marginTop: '8px' }}
                        >
                          <InfoRegular style={{ fontSize: '14px', marginRight: '4px' }} />
                          Request Edit Approval
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </Field>

              <div className={styles.fieldRow}>
                <div className={styles.fieldHalf}>
                  <Field label="Tax Identification Number (TIN)">
                    <div className={styles.lockedField}>
                      <Input
                        value={editMode ? editFormData.tin : companyData.tin}
                        onChange={e => handleFieldUpdate('tin', e.target.value)}
                        readOnly={!editMode}
                        className={!editMode ? styles.readOnlyField : ''}
                      />
                      {isFieldLocked('tin') && (
                        <div className={styles.lockIndicator}>
                          <LockClosedRegular style={{ fontSize: '12px' }} />
                          Locked
                        </div>
                      )}
                    </div>
                  </Field>
                </div>

                <div className={styles.fieldHalf}>
                  <Field label="Tax Registration Number">
                    <div className={styles.lockedField}>
                      <Input
                        value={
                          editMode
                            ? editFormData.tax_registration_number
                            : companyData.tax_registration_number
                        }
                        onChange={e => handleFieldUpdate('tax_registration_number', e.target.value)}
                        readOnly={!editMode}
                        className={!editMode ? styles.readOnlyField : ''}
                      />
                      {isFieldLocked('tax_registration_number') && (
                        <div className={styles.lockIndicator}>
                          <LockClosedRegular style={{ fontSize: '12px' }} />
                          Locked
                        </div>
                      )}
                    </div>
                  </Field>
                </div>
              </div>

              <Field label="Registered Address">
                <div className={styles.lockedField}>
                  <Input
                    value={
                      editMode ? editFormData.registered_address : companyData.registered_address
                    }
                    onChange={e => handleFieldUpdate('registered_address', e.target.value)}
                    readOnly={!editMode}
                    className={!editMode ? styles.readOnlyField : ''}
                  />
                  {isFieldLocked('registered_address') && (
                    <div className={styles.lockIndicator}>
                      <LockClosedRegular style={{ fontSize: '12px' }} />
                      Locked
                    </div>
                  )}
                </div>
              </Field>

              <Field label="Country">
                <div className={styles.lockedField}>
                  {editMode ? (
                    <SearchableDropdown
                      options={countries.map(c => ({
                        id: c.value,
                        label: c.label,
                        value: c.value,
                      }))}
                      selectedValue={editFormData.country || ''}
                      onSelect={value => handleFieldUpdate('country', value)}
                      placeholder="Select country"
                    />
                  ) : (
                    <Input value={countryLabel} readOnly className={styles.readOnlyField} />
                  )}
                  {isFieldLocked('country') && (
                    <div className={styles.lockIndicator}>
                      <LockClosedRegular style={{ fontSize: '12px' }} />
                      Locked
                    </div>
                  )}
                </div>
              </Field>
            </div>
          </div>
        </ContentCard>

        <ContentCard>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>
                <PhoneRegular className={styles.sectionIcon} />
                <Text>Contact Information</Text>
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <Field label="Primary Phone">
                <Input
                  value={editMode ? editFormData.primary_phone : companyData.primary_phone}
                  onChange={e => handleFieldUpdate('primary_phone', e.target.value)}
                  readOnly={!editMode}
                  className={!editMode ? styles.readOnlyField : ''}
                />
              </Field>

              {companyData.secondary_phones && companyData.secondary_phones.length > 0 && (
                <Field label="Secondary Phones">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {companyData.secondary_phones.map((phone, index) => (
                      <div key={index} className={styles.arrayItem}>
                        <PhoneRegular className={styles.arrayItemIcon} />
                        <Text>{phone}</Text>
                      </div>
                    ))}
                  </div>
                </Field>
              )}

              <Field label="Primary Email">
                <div className={styles.lockedField}>
                  <Input
                    value={editMode ? editFormData.primary_email : companyData.primary_email}
                    onChange={e => handleFieldUpdate('primary_email', e.target.value)}
                    readOnly={!editMode}
                    className={!editMode ? styles.readOnlyField : ''}
                  />
                  {isFieldLocked('primary_email') && (
                    <div className={styles.lockIndicator}>
                      <LockClosedRegular style={{ fontSize: '12px' }} />
                      Locked
                    </div>
                  )}
                </div>
              </Field>

              {companyData.secondary_emails && companyData.secondary_emails.length > 0 && (
                <Field label="Secondary Emails">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {companyData.secondary_emails.map((email, index) => (
                      <div key={index} className={styles.arrayItem}>
                        <MailRegular className={styles.arrayItemIcon} />
                        <Text>{email}</Text>
                      </div>
                    ))}
                  </div>
                </Field>
              )}

              <Field label="Website">
                <Input
                  value={editMode ? editFormData.website : companyData.website}
                  onChange={e => handleFieldUpdate('website', e.target.value)}
                  readOnly={!editMode}
                  className={!editMode ? styles.readOnlyField : ''}
                />
              </Field>
            </div>
          </div>
        </ContentCard>
      </div>

      <div className={styles.gridLayout}>
        <ContentCard>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>
                <ColorRegular className={styles.sectionIcon} />
                <Text>Branding</Text>
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <Field label="Primary Color">
                <div className={styles.colorDisplay}>
                  <div
                    className={styles.colorSwatch}
                    style={{ backgroundColor: companyData.primary_color }}
                  />
                  <div className={styles.colorInfo}>
                    <Text style={{ fontSize: '16px', fontWeight: '600' }}>Primary</Text>
                    <Text style={{ fontSize: '13px', color: '#605e5c' }}>
                      {companyData.primary_color}
                    </Text>
                  </div>
                </div>
              </Field>

              <Field label="Secondary Color">
                <div className={styles.colorDisplay}>
                  <div
                    className={styles.colorSwatch}
                    style={{ backgroundColor: companyData.secondary_color }}
                  />
                  <div className={styles.colorInfo}>
                    <Text style={{ fontSize: '16px', fontWeight: '600' }}>Secondary</Text>
                    <Text style={{ fontSize: '13px', color: '#605e5c' }}>
                      {companyData.secondary_color}
                    </Text>
                  </div>
                </div>
              </Field>

              <Field label="Accent Color">
                <div className={styles.colorDisplay}>
                  <div
                    className={styles.colorSwatch}
                    style={{ backgroundColor: companyData.accent_color }}
                  />
                  <div className={styles.colorInfo}>
                    <Text style={{ fontSize: '16px', fontWeight: '600' }}>Accent</Text>
                    <Text style={{ fontSize: '13px', color: '#605e5c' }}>
                      {companyData.accent_color}
                    </Text>
                  </div>
                </div>
              </Field>
            </div>
          </div>
        </ContentCard>

        <ContentCard>
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>
                <DocumentTextRegular className={styles.sectionIcon} />
                <Text>Document Prefixes</Text>
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <div className={styles.fieldRow}>
                <div className={styles.fieldHalf}>
                  <Field label="Invoice Prefix">
                    <Input
                      value={editMode ? editFormData.invoice_prefix : companyData.invoice_prefix}
                      onChange={e =>
                        handleFieldUpdate('invoice_prefix', e.target.value.toUpperCase())
                      }
                      readOnly={!editMode}
                      className={!editMode ? styles.readOnlyField : ''}
                    />
                  </Field>
                </div>

                <div className={styles.fieldHalf}>
                  <Field label="Memo Prefix">
                    <Input
                      value={editMode ? editFormData.memo_prefix : companyData.memo_prefix}
                      onChange={e => handleFieldUpdate('memo_prefix', e.target.value.toUpperCase())}
                      readOnly={!editMode}
                      className={!editMode ? styles.readOnlyField : ''}
                    />
                  </Field>
                </div>
              </div>

              <div className={styles.fieldRow}>
                <div className={styles.fieldHalf}>
                  <Field label="Incident Prefix">
                    <Input
                      value={editMode ? editFormData.incident_prefix : companyData.incident_prefix}
                      onChange={e =>
                        handleFieldUpdate('incident_prefix', e.target.value.toUpperCase())
                      }
                      readOnly={!editMode}
                      className={!editMode ? styles.readOnlyField : ''}
                    />
                  </Field>
                </div>

                <div className={styles.fieldHalf}>
                  <Field label="Contract Prefix">
                    <Input
                      value={editMode ? editFormData.contract_prefix : companyData.contract_prefix}
                      onChange={e =>
                        handleFieldUpdate('contract_prefix', e.target.value.toUpperCase())
                      }
                      readOnly={!editMode}
                      className={!editMode ? styles.readOnlyField : ''}
                    />
                  </Field>
                </div>
              </div>
            </div>
          </div>
        </ContentCard>
      </div>

      <ContentCard title="Version History" subtitle="Track all changes made to company information">
        <div id="version-history">
          {versionHistory.length === 0 ? (
            <div className={styles.emptyState}>
              <HistoryRegular className={styles.emptyIcon} />
              <Text>No version history available</Text>
            </div>
          ) : (
            versionHistory.map(version => {
              // Extract meaningful initials from changed_by (handle objects, numbers, etc.)
              let displayName = 'Unknown';
              let initials = 'UN';
              
              if (version.changed_by) {
                if (typeof version.changed_by === 'string') {
                  displayName = version.changed_by;
                } else if (typeof version.changed_by === 'object' && version.changed_by !== null) {
                  const changedByObj = version.changed_by as any;
                  if (changedByObj.name) {
                    displayName = changedByObj.name;
                  } else {
                    displayName = String(version.changed_by);
                  }
                } else if (typeof version.changed_by === 'number') {
                  displayName = `User ${version.changed_by}`;
                  initials = 'U' + String(version.changed_by).charAt(0);
                } else {
                  displayName = String(version.changed_by);
                }
                
                // Extract initials from displayName (avoid special characters)
                const cleanName = displayName.replace(/[^a-zA-Z0-9\s]/g, '').trim();
                if (cleanName.length > 0) {
                  const words = cleanName.split(/\s+/).filter(w => w.length > 0);
                  if (words.length >= 2 && words[0] && words[1]) {
                    initials = (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
                  } else if (words.length >= 1 && words[0]) {
                    initials = cleanName.substring(0, 2).toUpperCase();
                  }
                }
              }
              
              // Parse date safely
              let dateDisplay = 'Unknown date';
              if (version.changed_at) {
                try {
                  const date = new Date(version.changed_at);
                  if (!isNaN(date.getTime())) {
                    dateDisplay = date.toLocaleString();
                  }
                } catch (e) {
                  console.error('Invalid date format:', version.changed_at);
                }
              }
              
              return (
                <div key={version.id} className={styles.versionItem}>
                  <Avatar
                    className={styles.versionAvatar}
                    color="brand"
                    initials={initials}
                  />
                  <div className={styles.versionContent}>
                    <div className={styles.versionHeader}>
                      <Text className={styles.versionNumber}>Version {version.version}</Text>
                      <Text className={styles.versionDate}>{dateDisplay}</Text>
                    </div>
                    <Text className={styles.versionChanges}>
                      {version.changed_fields?.join(', ') || 'No changes'} updated
                  </Text>
                  {version.change_reason && (
                    <Text className={styles.versionMeta}>Reason: {version.change_reason}</Text>
                  )}
                  <Text className={styles.versionMeta}>Changed by: {displayName}</Text>
                </div>
              </div>
              );
            })
          )}
        </div>
      </ContentCard>

      {/* Edit Request Dialog */}
      <Dialog open={requestDialogOpen} onOpenChange={(_, data) => setRequestDialogOpen(data.open)}>
        <DialogSurface className={styles.editRequestDialog}>
          <DialogBody>
            <DialogTitle>Request Edit Approval</DialogTitle>
            <DialogContent>
              <div className={styles.dialogField}>
                <Field label="Field to Edit">
                  <Input value={FIELD_LABELS[requestField] || requestField} readOnly />
                </Field>
              </div>

              <div className={styles.dialogField}>
                <Field label="Current Value">
                  <Input
                    value={companyData[requestField as keyof CompanySetup] as string}
                    readOnly
                  />
                </Field>
              </div>

              <div className={styles.dialogField}>
                <Field label="Requested New Value" required>
                  <Input
                    value={requestValue}
                    onChange={e => setRequestValue(e.target.value)}
                    placeholder="Enter new value"
                  />
                </Field>
              </div>

              <div className={styles.dialogField}>
                <Field label="Reason for Change" required>
                  <Textarea
                    value={requestReason}
                    onChange={e => setRequestReason(e.target.value)}
                    placeholder="Explain why this change is needed..."
                    rows={4}
                  />
                </Field>
              </div>

              <div
                style={{
                  padding: '12px',
                  backgroundColor: '#fef3cd',
                  borderRadius: '6px',
                  border: '1px solid #fde68a',
                  marginTop: '16px',
                }}
              >
                <Text style={{ fontSize: '13px', color: '#92400e' }}>
                  <InfoRegular style={{ fontSize: '14px', marginRight: '4px' }} />
                  This field is locked and requires admin approval. Your request will be reviewed by
                  an administrator.
                </Text>
              </div>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRequestDialogOpen(false)}>Cancel</Button>
              <Button
                appearance="primary"
                onClick={handleRequestEdit}
                style={{ backgroundColor: '#008272', color: '#ffffff' }}
              >
                Submit Request
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default CompanySetupView;
